import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { forkJoin, Observable, pipe, Subject, timeInterval, Timestamp } from 'rxjs';
import { HostnameBuilderService } from './helpers/hostname-builder.service';
import { LogService } from './log.service';
import { CliffLogCodes } from '../shared/constants/cliff-log-constants';
import { parseJwt } from 'customer-oauth2-token-management-utility';
import { CookieHandlerService } from './helpers/cookie-handler.service';
import { formatDate } from '@angular/common';
import { exec } from 'child_process';
import { timeStamp } from 'console';

@Injectable({
  providedIn: 'root'
})
export class MediaUploadService {

  constructor(
    private http: HttpClient,
    private cookieService: CookieHandlerService,
    private logService: LogService,
    private hostnameBuilderService: HostnameBuilderService,
  ) {
    this.mediaUrl = this.hostnameBuilderService.getMediaUploadHostname() + '/media-upload/v3/startupload';
  }

  private mediaUrl;
  signedUrlsReceived = new Subject<any>();
  signedUrls: any;
  selectedFile: any[];
  files: File[];
  req: any[];
  requestBody: any[];
  customerFiles: any;
  successfullySubmitted: any[] = [];
  failedSubmission: any[] = [];
  mediaUploadStarted = new Subject<any>();
  mediaUploadPostCallCompleted = new Subject<any>();
  mediaUploadComplete = new Subject<any>();
  startTime: number;
  successEndTime: number;
  totalDuration: number;
  failureEndTime: number;
  waitingTime: number;

  startUpload(externalClaimId: string, externalParticipantId?: string) {
    this.startTime= performance.now();
    this.mediaUploadStarted.next(true);
    this.logService.log(CliffLogCodes.MEDIA_UPLOAD_START_UPLOAD_BEGIN);
    var httpOptions;
    let parsedJwt = parseJwt();
    const oauthHeader = this.cookieService.getOauthToken() ? 'Bearer ' + this.cookieService.getOauthToken() : '';
    if(parsedJwt){
      httpOptions = {
        headers: new HttpHeaders({
          'Authorization': oauthHeader,
          CorrelationId: this.logService.getCorrelationId(),
          'x-amz-meta-callerappid': 'OLRFNOL',
          }
        ),
        withCredentials: false
      };
    } else {
      this.logService.log(CliffLogCodes.MEDIA_UPLOAD_OKTA_FAIL);
    }

    this.executePostCall(0, externalClaimId, httpOptions, externalParticipantId);
    this.mediaUploadPostCallCompleted.subscribe((putCallArray) => {
      this.executePutCalls(putCallArray);
    });
  }

  private executePostCall(retryCount: number, externalClaimId: string, httpOptions: any, externalParticipantId?: string) {
    const mediaUploadEndpoint = this.mediaUrl + "?createGroup=false";
    this.http
      .post<any>(mediaUploadEndpoint, this.createRequestBody(externalClaimId, externalParticipantId), httpOptions)
      .subscribe({
        next: (response) => {
          const myResponse = response as any;
          this.signedUrls = myResponse.payload.links;
          this.signedUrlsReceived.next(this.signedUrls);
          let observableArray: Observable<any>[] = [];
          this.files.forEach(file => {
            if (file.size === 0) {
              const userAgent = navigator.userAgent || 'Unknown User Agent';
              this.logService.log({
                id: 'MEDIA_UPLOAD_FILE_SIZE_ZERO',
                text: `File size is zero for file: ${file.name} and device type is ${userAgent}`
              });
              this.failedSubmission.push({name: file.name});
              return;
            }
            const matchingLink = this.signedUrls.find(link => link.rel === file.name);
            if (matchingLink) {
              observableArray.push(this.uploadFile(file, matchingLink.href));
            } else {
              this.failedSubmission.push({name: file.name});
              console.log(`No match found for file: ${file.name}`); // for testing
            }
          });
          this.mediaUploadPostCallCompleted.next(observableArray);
        },
        error: () => {
          if (retryCount < 5) {
            this.logService.log({id: 'MEDIA_UPLOAD_RETRYING', text: 'Retrying media upload ' + (retryCount + 1) + ' time.'});
            setTimeout(() => {
              this.executePostCall(retryCount + 1, externalClaimId, httpOptions, externalParticipantId);
            }, 3000) // Retry after 3 seconds
          } else {
            this.logService.log(CliffLogCodes.MEDIA_UPLOAD_START_UPLOAD_FAIL);
            this.mediaUploadComplete.next(false);
          }
        }
      }
      );
  }

  executePutCalls(observableArray: Observable<any>[]) {
    forkJoin(observableArray).subscribe({
      next: (response) => {
        this.successEndTime = performance.now();
        this.totalDuration = (this.successEndTime - this.startTime)/1000;
        this.logService.log({id: 'MEDIA_UPLOAD_SUCCESSFUL', text: 'All media uploads were successful.'
        });
        this.mediaUploadComplete.next({
          successful: true,
          fileCount: this.files.length,
          successCount: this.files.length,
          failCount: 0,
        });
        this.logService.log(CliffLogCodes.MEDIA_UPLOAD_START_UPLOAD_COMPLETED,
          {
            photoUploadDuration: this.totalDuration
          }
        );
      },
      error: (error) => {
        this.logService.log({id: 'MEDIA_UPLOAD_UNSUCCESSFUL', text: 'Media upload unsuccessful on PUT calls.'});
        this.mediaUploadComplete.next({
          successful: false,
          fileCount: this.files.length,
          successCount: 0,
          failCount: this.files.length});
        this.logService.log(CliffLogCodes.MEDIA_UPLOAD_START_UPLOAD_COMPLETED);
        }
      });
  }

  uploadFile(file: File, signedUrl: string) {
    if (!file) {
        return;
    }

    let httpOptions: { headers?: HttpHeaders; withCredentials?: false };
    return this.http.put<any>(signedUrl, file, httpOptions);
  }

  getSuccessCount() {
    return this.successfullySubmitted.length;
  }

  getFailureCount() {
    return this.failedSubmission.length;
  }

  getFiles(): File[] {
    if (this.files) {
      return this.files;
    }

    const serializedFiles = JSON.parse(sessionStorage.getItem('uploadFiles') || '[]');

    // Reconstruct File objects from the serialized data
    this.files = serializedFiles.map((file: any) =>
      new File([new Blob()], file.name, {
        type: file.type,
        lastModified: file.lastModified
      })
    );

    return this.files || [];
  }

  setFiles(files: File[]): void {
    if (files) {
      this.files = files;

      // Serialize only the necessary properties of the File objects
      const serializedFiles = files.map(file => ({
        name: file.name,
        type: file.type,
        size: file.size,
        lastModified: file.lastModified
      }));

      sessionStorage.setItem('uploadFiles', JSON.stringify(serializedFiles));
    }
  }

  createRequestBody(externalClaimId: string, externalParticipantId?: string): any {
    let processFiles = [];
    if (externalParticipantId) {
      this.getFiles().forEach(file => {
        processFiles.push({
          name: file.name,
          metadata: JSON.stringify({
            captureSourceType: "OLR",
            categoryType: "IP",
            creatorIdentification: "01SYS",
            receivedTimestamp: formatDate(Date.now(), "yyyy-MM-ddThh:mm:ss.SSS'Z'", 'en-US'),
            fileName: file.name,
            agentViewable: false,
            fileUpload: {
              byParticipantExtId: externalParticipantId,
              key: "OLRFNOL"
            },
            participant: {
              participantExtId: externalParticipantId,
              viewableTo: true
            }
          })
        });
      });
    } else {
    this.getFiles().forEach(file => {
        processFiles.push({
          name: file.name,
          metadata: JSON.stringify({
            captureSourceType: "OLR",
            categoryType: "IP",
            creatorIdentification: "01SYS",
            receivedTimestamp: formatDate(Date.now(), "yyyy-MM-ddThh:mm:ss.SSS'Z'", 'en-US'),
            fileName: file.name,
            agentViewable: false,
            fileUpload: {
              key: "OLRFNOL"
            }
          })
        });
      });
    }
      const requestBody = {
        "categoryType": "IP",
        "externalClaimId": externalClaimId,
        "files": processFiles,
      };

      return requestBody;
  }
}


